import { takeEvery, select, fork, call, takeLatest } from 'redux-saga/effects'

import * as settings from 'settings'
import { LOCATION_CHANGE, pathSelector } from 'browser-location'
import { PURCHASE_COMPLETED } from 'basket/constants'
import { userSelector, accountKeySelector } from 'auth/selectors'
import { SET_AUTHENTICATED_USER } from 'auth/constants'
import { cookiesAcceptedSelector } from 'cookies/selectors'
import { SET_COOKIES } from 'cookies/constants'
import { deleteCookie } from 'cookies/utils'
import { CONFIGURATION_RECEIVED } from 'wordpress/constants'

const appendWithDot = value => `${value ? `${value}.` : ''}`

function hashCode(s) {
    return s.split('').reduce(function(a, b) {
        a = (a << 5) - a + b.charCodeAt(0)
        return a & a
    }, 0)
}

/* GA script provided by Google, no need to rewrite it */
const initGA = function(i, s, o, g, r, a, m) {
    i['GoogleAnalyticsObject'] = r
    ;(i[r] =
        i[r] ||
        function() {
            ;(i[r].q = i[r].q || []).push(arguments)
        }),
        (i[r].l = 1 * new Date())
    ;(a = s.createElement(o)), (m = s.getElementsByTagName(o)[0])
    a.async = 1
    a.src = g
    m.parentNode.insertBefore(a, m)
}

/* GTM script provided by Google, no need to rewrite it */
const initGTM = function(w, d, s, l, i) {
    w[l] = w[l] || []
    w[l].push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
    })
    const f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l !== 'dataLayer' ? `&l=${l}` : ''
    j.async = true
    j.src = `https://www.googletagmanager.com/gtm.js?id=${i}${dl}`
    f.parentNode.insertBefore(j, f)
}

const createTracker = (id, name, linkedDomains) => {
    ga('create', id, 'auto', { name: name || undefined, allowLinker: true })
    ga(`${appendWithDot(name)}require`, 'linker')
    ga('linker:autoLink', linkedDomains)
    ga(`${appendWithDot(name)}require`, 'ecommerce')
}

function* trackPageView() {
    if (!window.ga) return
    const trackers = Object.values(yield select(settings.selectors.googleTrackersSelector))
    const path = yield select(pathSelector)
    const args = ['pageview', path]

    if (trackers.length === 0) {
        ga('send', ...args)
    } else {
        trackers.forEach(tracker => (tracker ? ga(`${tracker}.send`, ...args) : ga('send', ...args)))
    }
}

function* watchLocationChangeSaga() {
    yield takeEvery(LOCATION_CHANGE, trackPageView)
}

function* setUserIdTrackerSaga() {
    yield takeEvery(SET_AUTHENTICATED_USER, function*() {
        if (!window.ga) return
        const accountKey = yield select(accountKeySelector)
        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        //ga
        ga('set', 'userId', accountKey)
        ga('send', 'event', 'authentication', 'user-id available')

        //gtm
        if (tagManagerId) {
            window.dataLayer.push({ userId: accountKey, event: 'authentication' })
        }
    })
}

function* googleAnalyticsSaga() {
    const cookiesAccepted = yield select(cookiesAcceptedSelector)
    if (cookiesAccepted?.analytics) {
        initGA(window, document, 'script', 'https://www.google-analytics.com/analytics.js', 'ga')
        const trackers = yield select(settings.selectors.googleTrackersSelector)

        const linkedDomains = yield select(settings.selectors.googleAnalyticsLinkedDomainsSelector)
        Object.keys(trackers).forEach(id => createTracker(id, trackers[id], linkedDomains))

        yield call(trackPageView)

        const tagManagerId = yield select(settings.selectors.googleTagManagerIdSelector)

        if (tagManagerId) {
            initGTM(window, document, 'script', 'dataLayer', tagManagerId)
        }
    } else if (!!window.ga && !cookiesAccepted?.analytics) {
        if (window.ga) window.ga('remove')
        deleteCookie('_ga')
        deleteCookie('_gid')
        deleteCookie('_gat')
    }
}

function* ecommerceSaga() {
    yield takeEvery(PURCHASE_COMPLETED, function* handleEcommerceTracking({ basket, paymentTotal }) {
        if (!window.ga) return
        const trackers = yield select(settings.selectors.googleTrackersSelector)
        const user = yield select(userSelector)
        const transactionId = hashCode(`${user.email}${Date.now()}`)

        const items = basket.items.map(item => ({
            id: `${transactionId}`,
            name: `${item.rate.rateName}`,
            sku: `${item.rate.locationId}-${item.rate.rateId}`,
            category: item.rate.isMonthlyPass ? 'SeasonTicket' : 'Booking',
            price: `${item.rate.amount}`,
            quantity: '1',
        }))

        const transaction = {
            id: `${transactionId}`,
            revenue: `${paymentTotal}`,
        }

        Object.keys(trackers)
            .map(id => trackers[id])
            .map(name => {
                ga(`${appendWithDot(name)}ecommerce:addTransaction`, transaction)
                items.map(item => {
                    ga(`${appendWithDot(name)}ecommerce:addItem`, item)
                })
                ga(`${appendWithDot(name)}ecommerce:send`)
            })
    })
}

export default function* rootSaga() {
    yield fork(watchLocationChangeSaga)
    yield fork(ecommerceSaga)
    yield fork(setUserIdTrackerSaga)
    yield takeEvery(SET_COOKIES, googleAnalyticsSaga)
    yield takeLatest(CONFIGURATION_RECEIVED, googleAnalyticsSaga)
}
